import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import { useSEO } from '@/hooks/useSEO';

interface LayoutConfig {
  nome: string;
  descricao: string;
  icon: string;
  areaPorEstacao: number;
  corredorMinimo: number;
  percentualReuniao: number;
  percentualArmazenamento: number;
  percentualCirculacao: number;
}

interface EstacaoConfig {
  nome: string;
  descricao: string;
  largura: number;
  profundidade: number;
  precoEstimado: number;
}

const layouts: LayoutConfig[] = [
  {
    nome: 'Open Space',
    descricao: 'Estações em ambiente aberto, ideal para colaboração',
    icon: 'ri-layout-grid-line',
    areaPorEstacao: 3.5,
    corredorMinimo: 1.2,
    percentualReuniao: 15,
    percentualArmazenamento: 8,
    percentualCirculacao: 12,
  },
  {
    nome: 'Salas Individuais',
    descricao: 'Escritórios com salas fechadas para cada colaborador',
    icon: 'ri-door-closed-line',
    areaPorEstacao: 8.0,
    corredorMinimo: 1.5,
    percentualReuniao: 20,
    percentualArmazenamento: 10,
    percentualCirculacao: 15,
  },
  {
    nome: 'Layout Misto',
    descricao: 'Combinação de open space com salas de reunião e diretoria',
    icon: 'ri-layout-masonry-line',
    areaPorEstacao: 5.0,
    corredorMinimo: 1.3,
    percentualReuniao: 18,
    percentualArmazenamento: 9,
    percentualCirculacao: 13,
  },
  {
    nome: 'Call Center',
    descricao: 'Estações compactas para atendimento telefônico em larga escala',
    icon: 'ri-customer-service-2-line',
    areaPorEstacao: 2.5,
    corredorMinimo: 1.0,
    percentualReuniao: 10,
    percentualArmazenamento: 5,
    percentualCirculacao: 10,
  },
];

const estacoes: EstacaoConfig[] = [
  {
    nome: 'Padrão',
    descricao: 'Mesa 1,20m x 0,60m com gaveteiro',
    largura: 1.2,
    profundidade: 0.6,
    precoEstimado: 2800,
  },
  {
    nome: 'Executiva',
    descricao: 'Mesa 1,60m x 0,80m com gaveteiro e apoio',
    largura: 1.6,
    profundidade: 0.8,
    precoEstimado: 4500,
  },
  {
    nome: 'Bench',
    descricao: 'Mesa compartilhada 1,40m x 0,70m por colaborador',
    largura: 1.4,
    profundidade: 0.7,
    precoEstimado: 2200,
  },
  {
    nome: 'Em L',
    descricao: 'Mesa em L 1,50m x 1,50m com gaveteiro',
    largura: 1.5,
    profundidade: 1.5,
    precoEstimado: 3800,
  },
];

const faqItems = [
  {
    pergunta: 'Como funciona a calculadora de mobiliário corporativo?',
    resposta: 'A calculadora estima quantas estações de trabalho cabem em um ambiente com base nas dimensões informadas (largura e comprimento em metros), no tipo de layout escolhido (open space, salas individuais, misto ou call center) e no modelo de estação (padrão, executiva, bench ou em L). Ela considera áreas mínimas para circulação, corredores, salas de reunião e armazenamento, conforme normas técnicas de ergonomia e conforto ambiental.',
  },
  {
    pergunta: 'Qual a área mínima por colaborador em um escritório?',
    resposta: 'A área mínima recomendada varia conforme o tipo de layout e a atividade. Para open space, recomenda-se no mínimo 3,5m² por colaborador. Para salas individuais, o mínimo é de 8m². Em call centers, onde as estações são mais compactas, o mínimo é de 2,5m². Esses valores incluem a estação de trabalho e a área de circulação imediata, mas não incluem salas de reunião, copas e banheiros.',
  },
  {
    pergunta: 'O cálculo inclui salas de reunião e copa?',
    resposta: 'Sim. A calculadora reserva automaticamente uma porcentagem da área total para salas de reunião (10% a 20% conforme o layout), armazenamento (5% a 10%) e circulação geral (10% a 15%). Esses percentuais são baseados em boas práticas de projeto corporativo e podem ser ajustados conforme a necessidade específica de cada empresa.',
  },
  {
    pergunta: 'Os preços estimados são precisos?',
    resposta: 'Os preços são estimativas baseadas em médias de mercado para mobiliário corporativo de qualidade (marcas como Frisokar, Plaxmetal, Marzo Vitorino e W3). O valor real pode variar conforme as especificações técnicas, acabamentos escolhidos, quantidade (descontos por volume), condições de pagamento e necessidades específicas do projeto. Para um orçamento preciso, recomendamos solicitar uma proposta personalizada.',
  },
  {
    pergunta: 'Posso usar a calculadora para reforma de escritório existente?',
    resposta: 'Sim. Informe as dimensões do ambiente que será reformado e a calculadora indicará a capacidade máxima de estações. No entanto, para reformas, recomendamos uma visita técnica para avaliar infraestrutura existente (tomadas, pontos de rede, iluminação) e identificar possíveis limitações que a calculadora não considera, como pilares, vigas e caixas de passagem.',
  },
  {
    pergunta: 'Qual a diferença entre layout open space e salas individuais?',
    resposta: 'Open space é um ambiente amplo e contínuo sem paredes internas, onde as estações são dispostas em bancadas compartilhadas. É mais econômico em termos de área (3,5m² por colaborador) e favorece a colaboração, mas oferece menos privacidade. Salas individuais são escritórios fechados para cada colaborador ou pequenos grupos, oferecendo privacidade e controle acústico, mas exigem mais área (8m² por colaborador) e investimento em divisórias.',
  },
];

export default function CalculadoraPage() {
  const [largura, setLargura] = useState<number | ''>(10);
  const [comprimento, setComprimento] = useState<number | ''>(15);
  const [layoutSelecionado, setLayoutSelecionado] = useState(0);
  const [estacaoSelecionada, setEstacaoSelecionada] = useState(0);
  const [faqAberto, setFaqAberto] = useState<number | null>(null);

  const layout = layouts[layoutSelecionado];
  const estacao = estacoes[estacaoSelecionada];

  const resultado = useMemo(() => {
    if (!largura || !comprimento || largura <= 0 || comprimento <= 0) return null;

    const areaTotal = largura * comprimento;
    const areaReuniao = areaTotal * (layout.percentualReuniao / 100);
    const areaArmazenamento = areaTotal * (layout.percentualArmazenamento / 100);
    const areaCirculacao = areaTotal * (layout.percentualCirculacao / 100);
    const areaUtil = areaTotal - areaReuniao - areaArmazenamento - areaCirculacao;

    const maxEstacoes = Math.floor(areaUtil / layout.areaPorEstacao);
    const estacoesPorFila = Math.floor((comprimento - layout.corredorMinimo) / (estacao.profundidade + 0.4));
    const filas = Math.floor((largura - layout.corredorMinimo) / (estacao.largura + 0.4));
    const estacoesPorGeometria = estacoesPorFila * filas;
    const estacoesRecomendadas = Math.min(maxEstacoes, estacoesPorGeometria);

    const mesasReuniao4 = Math.max(1, Math.floor(areaReuniao / 12));
    const mesasReuniao6 = Math.max(1, Math.floor(areaReuniao / 18));
    const mesasReuniao8 = Math.max(1, Math.floor(areaReuniao / 24));

    const armarios = Math.ceil(estacoesRecomendadas / 4);
    const gaveteiros = estacoesRecomendadas;

    const investimentoMobiliario = estacoesRecomendadas * estacao.precoEstimado;
    const investimentoReuniao = mesasReuniao4 * 3500 + mesasReuniao6 * 5000 + mesasReuniao8 * 7000;
    const investimentoArmazenamento = armarios * 1200 + gaveteiros * 400;
    const investimentoTotal = investimentoMobiliario + investimentoReuniao + investimentoArmazenamento;

    return {
      areaTotal,
      areaUtil,
      areaReuniao,
      areaArmazenamento,
      areaCirculacao,
      estacoesRecomendadas,
      estacoesPorFila,
      filas,
      mesasReuniao4,
      mesasReuniao6,
      mesasReuniao8,
      armarios,
      gaveteiros,
      investimentoMobiliario,
      investimentoReuniao,
      investimentoArmazenamento,
      investimentoTotal,
    };
  }, [largura, comprimento, layout, estacao]);

  useSEO({
    title: 'Calculadora de Mobiliário Corporativo | Quantas Mesas Cabem?',
    description: 'Calcule gratuitamente quantas estações de trabalho, mesas de reunião e armários cabem no seu escritório. Informe as dimensões do ambiente e receba um projeto estimado com investimento.',
    keywords: 'calculadora mobiliário corporativo, quantas mesas cabem escritório, dimensionar escritório, calculadora estações de trabalho, mobiliário corporativo pernambuco',
    canonical: 'https://homeofficepremium.com/calculadora',
    ogImage: 'https://homeofficepremium.com/og-image.jpg',
    robots: 'index, follow',
    dateModified: '2026-04-27',
  });

  const schemaSoftware = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Calculadora de Mobiliário Corporativo',
    applicationCategory: 'BusinessApplication',
    description: 'Ferramenta online que calcula quantas estações de trabalho, mesas de reunião e armários cabem em um escritório com base nas dimensões do ambiente.',
    url: 'https://homeofficepremium.com/calculadora',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Home Office Premium',
      url: 'https://homeofficepremium.com',
    },
  };

  const schemaFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.pergunta,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.resposta,
      },
    })),
  };

  const schemaBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://homeofficepremium.com/' },
      { '@type': 'ListItem', position: 2, name: 'Calculadora de Mobiliário', item: 'https://homeofficepremium.com/calculadora' },
    ],
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR').format(value);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <WhatsAppFloat />

      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaSoftware) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaFAQ) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaBreadcrumb) }} />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-14 md:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
            <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-600 font-medium">Calculadora de Mobiliário</span>
          </nav>
          <div className="max-w-2xl">
            <span className="text-primary-600 text-[11px] font-semibold tracking-[0.2em] uppercase">Ferramenta gratuita</span>
            <h1 className="mt-4 text-[32px] md:text-[48px] font-semibold text-foreground-900 leading-[1.08] tracking-tight">
              Calculadora de Mobiliário Corporativo
            </h1>
            <p className="mt-5 text-[15px] md:text-base text-foreground-500 leading-relaxed font-light max-w-xl">
              Descubra <strong className="text-foreground-900 font-medium">quantas estações de trabalho</strong>, mesas de reunião e armários cabem no seu escritório. Informe as dimensões e receba um projeto estimado em segundos.
            </p>
          </div>
        </div>
      </section>

      {/* Calculadora */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Painel de configuração */}
            <div className="lg:col-span-2 space-y-6">
              {/* Dimensões */}
              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-ruler-line text-primary-600"></i>
                  Dimensões do Ambiente
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1.5">Largura (metros)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      step="0.5"
                      value={largura}
                      onChange={(e) => setLargura(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-500 transition-all text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1.5">Comprimento (metros)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      step="0.5"
                      value={comprimento}
                      onChange={(e) => setComprimento(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-500 transition-all text-base"
                    />
                  </div>
                </div>
                {resultado && (
                  <p className="text-xs text-foreground-400 mt-3">
                    Área total: <strong className="text-foreground-700">{formatNumber(resultado.areaTotal)} m²</strong>
                  </p>
                )}
              </div>

              {/* Layout */}
              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-layout-line text-primary-600"></i>
                  Tipo de Layout
                </h2>
                <div className="space-y-2">
                  {layouts.map((l, i) => (
                    <button
                      key={l.nome}
                      onClick={() => setLayoutSelecionado(i)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer ${
                        layoutSelecionado === i
                          ? 'bg-foreground-900 text-white'
                          : 'bg-background-50 text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${layoutSelecionado === i ? 'bg-white/10' : 'bg-background-100'}`}>
                        <i className={`${l.icon} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{l.nome}</p>
                        <p className={`text-xs ${layoutSelecionado === i ? 'text-white/60' : 'text-foreground-400'}`}>{l.descricao}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Estação */}
              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-armchair-line text-primary-600"></i>
                  Modelo de Estação
                </h2>
                <div className="space-y-2">
                  {estacoes.map((e, i) => (
                    <button
                      key={e.nome}
                      onClick={() => setEstacaoSelecionada(i)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer ${
                        estacaoSelecionada === i
                          ? 'bg-foreground-900 text-white'
                          : 'bg-background-50 text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${estacaoSelecionada === i ? 'bg-white/10' : 'bg-background-100'}`}>
                        <i className="ri-table-line text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{e.nome}</p>
                        <p className={`text-xs ${estacaoSelecionada === i ? 'text-white/60' : 'text-foreground-400'}`}>{e.descricao}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Resultados */}
            <div className="lg:col-span-3">
              {resultado && (
                <div className="space-y-6">
                  {/* Cards principais */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-primary-600 rounded-2xl p-5 text-white">
                      <p className="text-xs text-white/70 mb-1">Estações</p>
                      <p className="text-3xl font-semibold">{formatNumber(resultado.estacoesRecomendadas)}</p>
                      <p className="text-xs text-white/60 mt-1">colaboradores</p>
                    </div>
                    <div className="bg-foreground-900 rounded-2xl p-5 text-white">
                      <p className="text-xs text-white/50 mb-1">Área Útil</p>
                      <p className="text-3xl font-semibold">{formatNumber(resultado.areaUtil)}</p>
                      <p className="text-xs text-white/50 mt-1">m² para estações</p>
                    </div>
                    <div className="bg-foreground-900 rounded-2xl p-5 text-white">
                      <p className="text-xs text-white/50 mb-1">Investimento Est.</p>
                      <p className="text-2xl font-semibold">{formatCurrency(resultado.investimentoTotal)}</p>
                      <p className="text-xs text-white/50 mt-1">mobiliário completo</p>
                    </div>
                    <div className="bg-foreground-900 rounded-2xl p-5 text-white">
                      <p className="text-xs text-white/50 mb-1">Por Colaborador</p>
                      <p className="text-2xl font-semibold">
                        {formatCurrency(resultado.investimentoTotal / Math.max(1, resultado.estacoesRecomendadas))}
                      </p>
                      <p className="text-xs text-white/50 mt-1">investimento médio</p>
                    </div>
                  </div>

                  {/* Distribuição de área */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-5">Distribuição da Área</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Estações de Trabalho', valor: resultado.areaUtil, cor: 'bg-primary-600' },
                        { label: 'Salas de Reunião', valor: resultado.areaReuniao, cor: 'bg-foreground-400' },
                        { label: 'Armazenamento', valor: resultado.areaArmazenamento, cor: 'bg-foreground-500' },
                        { label: 'Circulação', valor: resultado.areaCirculacao, cor: 'bg-foreground-300' },
                      ].map((item) => {
                        const percentual = (item.valor / resultado.areaTotal) * 100;
                        return (
                          <div key={item.label}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm text-foreground-600">{item.label}</span>
                              <span className="text-sm font-medium text-foreground-900">{formatNumber(item.valor)} m² ({percentual.toFixed(1)}%)</span>
                            </div>
                            <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                              <div className={`h-full ${item.cor} rounded-full transition-all duration-500`} style={{ width: `${percentual}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Detalhamento */}
                  <div className="bg-white rounded-2xl p-6">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-5">Detalhamento do Projeto</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-background-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-table-line text-primary-600 text-sm"></i>
                          <p className="text-sm font-medium text-foreground-900">Estações de Trabalho</p>
                        </div>
                        <p className="text-2xl font-semibold text-foreground-900">{formatNumber(resultado.estacoesRecomendadas)}</p>
                        <p className="text-xs text-foreground-400 mt-1">{resultado.filas} filas × {resultado.estacoesPorFila} estações</p>
                      </div>

                      <div className="bg-background-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-team-line text-foreground-600 text-sm"></i>
                          <p className="text-sm font-medium text-foreground-900">Mesas de Reunião</p>
                        </div>
                        <div className="space-y-1 mt-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">4 lugares</span>
                            <span className="font-medium text-foreground-900">{resultado.mesasReuniao4}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">6 lugares</span>
                            <span className="font-medium text-foreground-900">{resultado.mesasReuniao6}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">8 lugares</span>
                            <span className="font-medium text-foreground-900">{resultado.mesasReuniao8}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-background-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-archive-drawer-line text-foreground-600 text-sm"></i>
                          <p className="text-sm font-medium text-foreground-900">Armazenamento</p>
                        </div>
                        <div className="space-y-1 mt-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">Armários</span>
                            <span className="font-medium text-foreground-900">{resultado.armarios}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">Gaveteiros</span>
                            <span className="font-medium text-foreground-900">{resultado.gaveteiros}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-background-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-money-dollar-circle-line text-foreground-600 text-sm"></i>
                          <p className="text-sm font-medium text-foreground-900">Investimento</p>
                        </div>
                        <div className="space-y-1 mt-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">Mobiliário</span>
                            <span className="font-medium text-foreground-900">{formatCurrency(resultado.investimentoMobiliario)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">Reunião</span>
                            <span className="font-medium text-foreground-900">{formatCurrency(resultado.investimentoReuniao)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground-500">Armazenamento</span>
                            <span className="font-medium text-foreground-900">{formatCurrency(resultado.investimentoArmazenamento)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-foreground-950 rounded-2xl p-8 md:p-10 text-center">
                    <h3 className="text-xl font-semibold text-white mb-2">Quer um projeto personalizado?</h3>
                    <p className="text-white/50 text-sm mb-7 max-w-lg mx-auto font-light">
                      Esta calculadora é uma estimativa. Para um projeto preciso com planta baixa, projeto 3D e orçamento detalhado, fale com nossos consultores.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href={`https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Usei%20a%20calculadora%20do%20site%20e%20preciso%20de%20um%20or%C3%A7amento%20para%20${resultado.estacoesRecomendadas}%20esta%C3%A7%C3%B5es%20em%20${resultado.areaTotal}m%C2%B2`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 bg-primary-600 text-white text-sm font-medium px-6 py-3.5 rounded-full hover:bg-primary-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Solicitar orçamento pelo WhatsApp
                      </a>
                      <Link
                        to="/contato"
                        className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/20 text-sm font-medium px-6 py-3.5 rounded-full hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Enviar por e-mail
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="text-[24px] md:text-[32px] font-semibold text-foreground-900 text-center leading-[1.1] tracking-tight mb-3">
            Perguntas Frequentes sobre a Calculadora
          </h2>
          <p className="text-foreground-500 text-center mb-12 font-light">
            Tire suas dúvidas sobre como dimensionar corretamente seu escritório
          </p>

          <div className="divide-y divide-background-200/70 border-y border-background-200/70">
            {faqItems.map((item, i) => (
              <div key={i}>
                <button
                  onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
                >
                  <span className="text-[15px] md:text-base font-medium text-foreground-800 pr-6 leading-snug transition-colors group-hover:text-primary-600">
                    {item.pergunta}
                  </span>
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${faqAberto === i ? 'bg-primary-600 text-white rotate-180' : 'bg-background-100 text-foreground-500'}`}>
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </span>
                </button>
                {faqAberto === i && (
                  <div className="pb-6">
                    <p className="text-[14px] text-foreground-500 leading-relaxed font-light max-w-2xl">{item.resposta}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Links relacionados */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <h2 className="text-[22px] font-semibold text-foreground-900 mb-8">Conteúdo relacionado</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Glossário Corporativo', path: '/glossario', desc: 'Dicionário de termos técnicos', icon: 'ri-book-open-line' },
              { label: 'Perguntas Frequentes', path: '/faq', desc: '40+ respostas sobre mobiliário', icon: 'ri-questionnaire-line' },
              { label: 'Cadeiras Ergonômicas', path: '/cadeiras', desc: 'NR-17, Frisokar e Plaxmetal', icon: 'ri-armchair-line' },
              { label: 'Projetos Turnkey', path: '/turnkey', desc: 'Obras corporativas completas', icon: 'ri-building-line' },
            ].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="flex items-center gap-4 p-5 bg-white rounded-2xl hover:bg-background-50 transition-colors group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                  <i className={`${link.icon} text-primary-600 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">{link.label}</p>
                  <p className="text-xs text-foreground-400">{link.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}